import React, { ReactNode } from 'react'
import { Draggable, DraggableStateSnapshot } from 'react-beautiful-dnd'
import { useMinimalsTheme } from '../../../hooks'
import { GroupingContextAction } from './GroupingContextMenu'
import { GroupingNodeItem } from './GroupingNodeItem'
import { Group, Id, Item } from './types'

const primaryButton = 0

interface Props {
  group: Group
  item: Item
  index: number
  isSelected: boolean
  isGhosting: boolean
  selectionCount: number
  actions?: GroupingContextAction[]
  toggleSelection: (itemId: Id) => void
  toggleSelectionInGroup: (itemId: Id) => void
  multiSelectTo: (itemId: Id) => void
  renderData?: (item: Item, group: Group) => ReactNode
}

const GroupingItem = ({
  group,
  item,
  index,
  isSelected,
  isGhosting,
  selectionCount,
  actions,
  toggleSelection,
  toggleSelectionInGroup,
  multiSelectTo,
  renderData,
}: Props) => {
  const { palette } = useMinimalsTheme()
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, snapshot: DraggableStateSnapshot) => {
    if (event.defaultPrevented) {
      return
    }

    if (snapshot.isDragging) {
      return
    }

    event.preventDefault()

    performAction(event)
  }

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) {
      return
    }

    if (event.button !== primaryButton) {
      return
    }

    event.preventDefault()

    performAction(event)
  }

  const onTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) {
      return
    }

    event.preventDefault()
    toggleSelectionInGroup(item.id)
  }

  const wasToggleInSelectionGroupKeyUsed = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    //Check if using windows or mac
    const isUsingWindows = navigator.userAgent.indexOf('Win') >= 0
    return isUsingWindows ? event.ctrlKey : event.metaKey
  }

  const wasMultiSelectKeyUsed = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => event.shiftKey

  const performAction = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      toggleSelectionInGroup(item.id)
      return
    }

    if (wasMultiSelectKeyUsed(event)) {
      multiSelectTo(item.id)
      return
    }

    toggleSelection(item.id)
  }

  return (
    <Draggable
      key={`grp-dbid-${item.id}`}
      draggableId={item.id}
      index={index}
      isDragDisabled={group.isDragDisabled}
    >
      {(provided, snapshot) => {
        return GroupingNodeItem({
          provided,
          item,
          group,
          palette,
          selectionCount,
          isDragging: snapshot.isDragging,
          isGhosting,
          isSelected,
          snapshot,
          actions,
          renderData,
          onClick,
          onTouchEnd,
          onKeyDown,
        })
      }}
    </Draggable>
  )
}

export default GroupingItem
